import { useSearchParams } from 'react-router-dom';

import { CsAccessDenied } from '@/components/cs/PageStates';
import { Tabs } from '@/components/ui/Tabs';
import { hasCapability } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';

import { AgentsTab } from './operations/AgentsTab';
import { CredentialsTab } from './operations/CredentialsTab';
import { ErrorsTab } from './operations/ErrorsTab';
import { HealthTab } from './operations/HealthTab';
import { OpsAgentTab } from './operations/OpsAgentTab';
import { ReportsTab } from './operations/ReportsTab';
import { RoutingTab } from './operations/RoutingTab';
import { SecurityTab } from './operations/SecurityTab';

/**
 * CS Operations — /cs/operations (Phase 16 / ADR-026, spec §3.10).
 *
 * URL-driven tab shell (`?tab=health|agents|routing|credentials|
 * security|errors|agent|reports`, default health) so every tab is
 * linkable. Page requires `view_cs_operations`; per-tab WRITES are
 * additionally gated client-side (manage_cs_routing / manage_cs_agents
 * / manage_cs_credentials) — the server re-enforces everything.
 */

const TAB_IDS = [
  'health',
  'agents',
  'routing',
  'credentials',
  'security',
  'errors',
  'agent',
  'reports',
] as const;

type TabId = (typeof TAB_IDS)[number];

const TABS: { id: TabId; label: string }[] = [
  { id: 'health', label: 'Health' },
  { id: 'agents', label: 'Agents' },
  { id: 'routing', label: 'Routing' },
  { id: 'credentials', label: 'Credentials' },
  { id: 'security', label: 'Security' },
  { id: 'errors', label: 'Errors' },
  { id: 'agent', label: 'Ops Agent' },
  { id: 'reports', label: 'Reports' },
];

export function CsOperationsPage() {
  const { state: authState } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const canView = me !== null && hasCapability(me, 'view_cs_operations');

  const rawTab = searchParams.get('tab');
  const activeTab: TabId = TAB_IDS.includes(rawTab as TabId) ? (rawTab as TabId) : 'health';

  if (!me || !canView) {
    return <CsAccessDenied label="CS Operations needs the view_cs_operations capability." />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div>
        <h1 className="typo-h1">CS Operations</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Health checks, agents, routing, credentials, and reporting for the CS module.
        </p>
      </div>

      <Tabs
        tabs={TABS}
        activeTab={activeTab}
        onChange={(id) => {
          setSearchParams({ tab: id });
        }}
      />

      {activeTab === 'health' ? <HealthTab /> : null}
      {activeTab === 'agents' ? <AgentsTab me={me} /> : null}
      {activeTab === 'routing' ? <RoutingTab me={me} /> : null}
      {activeTab === 'credentials' ? <CredentialsTab me={me} /> : null}
      {activeTab === 'security' ? <SecurityTab me={me} /> : null}
      {activeTab === 'errors' ? <ErrorsTab /> : null}
      {activeTab === 'agent' ? <OpsAgentTab /> : null}
      {activeTab === 'reports' ? <ReportsTab /> : null}
    </div>
  );
}
